import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';

const ReportsDashboard = () => {
    const { user } = useContext(AuthContext);
    const [metrics, setMetrics] = useState(null);
    const [loading, setLoading] = useState(true);
    
    // Default to last 7 days
    const defaultEnd = new Date().toISOString().split('T')[0];
    const defaultStart = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    
    const [startDate, setStartDate] = useState(defaultStart);
    const [endDate, setEndDate] = useState(defaultEnd);

    const fetchMetrics = async () => {
        setLoading(true);
        try {
            const res = await axios.get('/reports/dashboard', {
                params: {
                    start_date: startDate,
                    end_date: endDate
                }
            });
            setMetrics(res.data);
        } catch (err) {
            console.error("Failed to fetch metrics", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMetrics();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate, endDate]);

    if (!metrics && loading) {
        return <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-color)' }}>Loading Dashboard...</div>;
    }

    const cardStyle = {
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(10px)',
        border: '1px solid var(--card-border)',
        borderRadius: '16px',
        padding: '20px',
        flex: '1',
        minWidth: '200px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'flex-start',
        boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.1)',
        position: 'relative',
        overflow: 'hidden'
    };

    const valueStyle = {
        fontSize: '2.5rem',
        fontWeight: 'bold',
        margin: '10px 0 0 0',
        zIndex: 1
    };

    const labelStyle = {
        fontSize: '1rem',
        color: 'var(--text-color)',
        opacity: 0.8,
        margin: 0,
        zIndex: 1,
        textTransform: 'uppercase',
        letterSpacing: '1px'
    };

    return (
        <div style={{ marginBottom: '40px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '15px' }}>
                <h2 style={{ margin: 0, color: 'var(--text-color)' }}>Performance Dashboard</h2>
                <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <label style={{ fontSize: '12px', color: 'var(--text-color)', opacity: 0.8, marginBottom: '4px' }}>Start Date</label>
                        <input 
                            type="date" 
                            value={startDate} 
                            onChange={(e) => setStartDate(e.target.value)}
                            style={{ padding: '8px', borderRadius: '8px', border: '1px solid var(--card-border)', background: 'var(--card-bg)', color: 'var(--text-color)' }}
                        />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <label style={{ fontSize: '12px', color: 'var(--text-color)', opacity: 0.8, marginBottom: '4px' }}>End Date</label>
                        <input 
                            type="date" 
                            value={endDate} 
                            onChange={(e) => setEndDate(e.target.value)}
                            style={{ padding: '8px', borderRadius: '8px', border: '1px solid var(--card-border)', background: 'var(--card-bg)', color: 'var(--text-color)' }}
                        />
                    </div>
                </div>
            </div>

            <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                {/* Green Tile: Open Spots */}
                <div style={{ ...cardStyle, borderTop: '4px solid #10b981' }}>
                    <div style={{ ...labelStyle }}>Open Spots</div>
                    <div style={{ ...valueStyle, color: '#10b981' }}>{metrics?.open_spots || 0}</div>
                </div>

                {/* Red Tile: Paid Spots */}
                <div style={{ ...cardStyle, borderTop: '4px solid #ef4444' }}>
                    <div style={{ ...labelStyle }}>Paid Spots</div>
                    <div style={{ ...valueStyle, color: '#ef4444' }}>{metrics?.paid_spots || 0}</div>
                </div>

                {/* Gold Tile: Revenue */}
                <div style={{ ...cardStyle, borderTop: '4px solid #f59e0b' }}>
                    <div style={{ ...labelStyle }}>Total Revenue</div>
                    <div style={{ ...valueStyle, color: '#f59e0b' }}>
                        ${((metrics?.total_revenue || 0) / 100).toFixed(2)}
                    </div>
                </div>

                {/* Info Tile: Utilization */}
                <div style={{ ...cardStyle, borderTop: '4px solid #3b82f6' }}>
                    <div style={{ ...labelStyle }}>Fleet Utilization</div>
                    <div style={{ ...valueStyle, color: '#3b82f6' }}>{metrics?.utilization_rate || 0}%</div>
                </div>
                
                {/* Warning Tile: Overrides */}
                <div style={{ ...cardStyle, borderTop: '4px solid #f97316' }}>
                    <div style={{ ...labelStyle }}>Manual Overrides</div>
                    <div style={{ ...valueStyle, color: '#f97316' }}>{metrics?.overrides || 0}</div>
                </div>

                {/* Fleet Size */}
                <div style={{ ...cardStyle, borderTop: '4px solid #8b5cf6' }}>
                    <div style={{ ...labelStyle }}>Active Fleet</div>
                    <div style={{ ...valueStyle, color: '#8b5cf6' }}>{metrics?.total_active_vehicles || 0}</div>
                </div>
            </div>

            {user?.role === 'admin' && metrics?.revenue_breakdown && metrics.revenue_breakdown.length > 0 && (
                <div style={{ marginTop: '30px', background: 'rgba(255,255,255,0.02)', padding: '20px', borderRadius: '16px', border: '1px solid var(--card-border)' }}>
                    <h3 style={{ margin: '0 0 15px 0', color: 'var(--text-color)' }}>Revenue Breakdown by Lot</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {metrics.revenue_breakdown.map((lot, index) => (
                            <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px', background: 'rgba(0,0,0,0.2)', borderRadius: '8px' }}>
                                <span style={{ color: 'var(--text-color)', fontWeight: 'bold' }}>{lot.lot_name}</span>
                                <span style={{ color: '#f59e0b', fontSize: '1.2rem', fontWeight: 'bold' }}>${(lot.amount / 100).toFixed(2)}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ReportsDashboard;
